/**
 * **读态判定的唯一一份实现**（四端共用；`src/core/` 按仓库对拷，靠 git blob 哈希证明同一份）。
 *
 * 本文件刻意 **零 import**——不引 `vue`、不引 `@/core` 别名、不引 `ApiError`：
 * 判据掺进依赖才能跑的代码，就没有离线机械复算的落点，验收只能靠人肉截图。零 import 使
 * `node --experimental-strip-types` 可以直接跑 `__assert__/readState.assert.ts`，**断言跑的
 * 就是应用跑的那段代码**。响应式外壳在 `composables/useReadState.ts`，它只把这里接到 ref 上。
 *
 * ── 为什么要有这一份 ────────────────────────────────────────────────────────────────
 * `rosterPayload.ts` 里那套判定（审批人名册）是对的，但它被写成了「名册专用」。而同一类缺陷
 * ——**读取失败却渲染成「没有 X」**——在全仓 10 处空态渲染点上都成立（见
 * `UI-SCREEN-DIFF-FRONTEND-20260911.md` §6.4）。每处各抄一遍判定必然漂移，所以判据只留这一份，
 * `rosterPayload.ts` 改为转引本文件（名册只剩「契约投影」这一件自己的事）。
 *
 * ── 三条不可混的事实（沿用 seq577 #83／seq586 ⑥／seq597 ⑤，未做任何放宽）────────────
 * 1. 成功支有两种合法形状：**裸数组** 或 分页壳 `{items:[…],total,…}`（定义件 §4.2／§6.1／§6.2）。
 *    两者都算 `ok`，不得因为形状不同而判失败。
 * 2. **真空**（`[]` 或 `items: []`）才是「没有 X」，只有 `kind:'ok'` 这一格允许走空态。
 * 3. `data:null`／载荷缺席／类型不对 ⇒ **畸形**（接口形状问题）；403 ⇒ 权限被拒；401／5xx／网络 ⇒
 *    读取失败。三者都**只出原因、不出空态**——「读不到」不等于「没有」。
 *
 * `failed` 一支把 401 也收进来：定义件 §6.2 里未带令牌＝401，而 401 与 5xx 的处置同为「不渲染空态、
 * 给原因」，无须为它单开一格（这也是我对定版件 §6.1「七态」只回五格的依据，见 §6.3）。
 *
 * 第 4 条（2026-09-11 20:1x 更新，**产品 seq773 §一 已就地落进 PRD `:69`＝AC-ROLE-7 第 5 条**）：
 * **渲染面也 ⛔ 读 `code`**。被派生的一方不能反过来当真源，兜底读它还会在前端另建第二张映射表
 * （产品点名的两因）；最硬一条是可机械预测的后果——`code` 的类型要等 #64（`:375` 甲、RK18-2 同批）
 * 才从码名翻成 `int`，而这枚兜底的触发条件（`resultCode` 缺席）恰是 #64 要消灭的状态 ⇒ 落地当天
 * 查表键变成 `400`，**兜底支会在契约修好那一刻自己崩成空白**。
 * ⇒ 定因**只认 `resultCode`**；缺它就没有定因，渲染走 `NO_CARRIER_COPY` 那句定版文案 ＋ 原样展示
 * `message`（⛔ 显示它不违规：`:55` 禁的是拿 `message` **定因**，不是 ⛔ 显示它）。
 */

/**
 * 「这次这枚业务码是从哪枚键读到的」。三格都是**事实**，不是评价，且**用途限定＝取证**：
 * ⛔ 参与判定、⛔ 把 PRD `:333` 的「不可判」升成「通过」（产品 seq773 一 原话）。
 *
 * · `resultCode` —— 唯一载体在册，定因成立。
 * · `code` —— **不是"这次用了 code"**。按 `:69` 那一枚**不会被读**，这格只登记「盘上只给了被派生
 *   的那一枚」这一事实；它就是 `D-11 :333`「无可寻址载体值 ⇒ 记不可判」在前端的对应物。
 * · `none` —— 两枚都没有值（或只有空串／纯空白，产品明令业务码禁止写成空串 ⇒ 空串＝缺载体）。
 */
export type BusinessCodeSource = 'resultCode' | 'code' | 'none';

/** 读取的唯一状态源。除 `ok` 之外没有任何一格可以渲染成「没有 X」。 */
export type ReadState =
  | { kind: 'idle' }
  /** 2xx 且形状合契约（含真空列表） */
  | { kind: 'ok' }
  | { kind: 'forbidden'; code?: string; message?: string; httpStatus: number; codeSource?: BusinessCodeSource }
  /** 含 401／400／4xx 非 403／5xx／网络中断：只出原因 */
  | { kind: 'failed'; code?: string; message?: string; httpStatus?: number; codeSource?: BusinessCodeSource }
  /** HTTP 2xx 但载荷形状不合契约（`null`／无 `items`／非数组）——接口形状问题，不是「没有 X」 */
  | { kind: 'malformed'; detail: string };

/**
 * `core/http/client.ts` 的 `ApiError` 里本判据真正需要的几样，按结构取，不引类型依赖。
 * `codeSource` 与 `message` 是投影的**输出**（`readErrorShapeOf` 必带前两枚），入参面上不必填。
 *
 * ⚠ `businessCode` **可以为缺值**——缺值就是缺值，⛔ 拿 `'UNKNOWN'` 之类的哨兵把它填上：
 * 那等于给一句"看起来像码"的字符串开进渲染面，而 `:69` 之后没有人能分辨它是真码还是占位。
 */
export interface ReadErrorShape {
  isForbidden?: boolean;
  businessCode?: string;
  message?: string;
  httpStatus?: number;
  codeSource?: BusinessCodeSource;
}

/** 非 2xx 的两格。单列一枚名字，是为了让「403 与读取失败」这组判据在类型面上就可指名引用。 */
export type ReadFailure = Extract<ReadState, { kind: 'forbidden' | 'failed' }>;

/**
 * **全仓唯一一处**「捕获对象 → 判据入参」的字段投影。
 *
 * 抛出来的东西有两种形状，都得认：`ApiError`（带 `httpStatus` 与 `isForbidden` getter）与
 * 裸 axios 错误（只有 `response.status`）。写成 structural 而非 import `ApiError`，是为了让
 * 本文件保持零 import——一旦引了 `client.ts`，node 就断言不到这枚投影，而它恰恰是最该被断言的一行
 * （投影错一格，403 就会掉进 `failed`，「无权限」与「读不到」又被并成一句话）。
 *
 * ── 定因键：只认 `resultCode` 一枚（PRD `:69`＝AC-ROLE-7 第 5 条，产品 seq773 一）──────────────
 * `:55` 写死「`resultCode: string` ＝ 业务码唯一载体」，`D-11 :332` 与 `RK16 :494` 的箭头都是
 * **`resultCode → code` 单向**⇒ 被派生的一方不能反过来当真源。
 * ⚠ **本文件曾在 2026-09-11 傍晚写过一版「缺则落 `code`」，已被产品 seq773 否决并在此撤回**；
 * 撤回的硬依据是可机械预测的那条后果：`code` 要等 #64 才翻成 `int`，而兜底的触发条件（`resultCode`
 * 缺席）恰是 #64 要消灭的状态 ⇒ 落地当天查表键变成 `400`，兜底支自己在契约修好那一刻崩成空白。
 * ⇒ 现在 `code` 只被**看一眼以记录事实**（`codeSource`，用途限定＝取证），⛔ 成为定因。
 * ⛔ 在本文件之外再投影一次这两枚键；⛔ 在这里建码名→人话的映射表（＝前端第二真源，同 `:63`
 * 拒 `PermissionCatalog` 并集那一族）。
 * ⚠ **本函数只服务渲染面**（拼一句给人看的因）。验收面按哪枚键判通过／不通过仍按 PRD `:333`
 * 记「不可判」——`codeSource !== 'resultCode'` 那些格就是它在前端的对应物，⛔ 任何人拿它当「已通过」。
 * ⛔ 禁止在此解析 `message` 文案定因（AC-ROLE-7 `:69` 原文点名）；本函数只是把 `message` **原样带过去**
 * 供缺载体那支定版文案展示，⛔ 在本层判断它的内容。
 */
export function readErrorShapeOf(err: unknown): ReadErrorShape & { codeSource: BusinessCodeSource } {
  const e = err as
    | {
        isForbidden?: boolean;
        message?: string;
        resultCode?: string | number;
        code?: string | number;
        httpStatus?: number;
        response?: { status?: number; data?: { message?: string } };
      }
    | null
    | undefined;
  const httpStatus = e?.httpStatus ?? e?.response?.status;
  const fromResult = pickBusinessCode(e?.resultCode);
  // 只用来记事实：有它说明「盘上此刻只给了被派生那一枚」，⛔ 把它当 businessCode 用。
  const derivedOnly = pickBusinessCode(e?.code) !== undefined;
  return {
    // 只认 getter 会把「裸 axios 403」判成 failed——对管理员这是把「无权限」说成「读不到」，
    // 两回事。`ApiError.isForbidden` 本身也是推导值（§6.0-④ 那组码），对 403 不会显式为 false，
    // 所以「getter 为真 ∨ 状态码就是 403」的并集只朝正确方向放宽，不会把非 403 拉成 403。
    isForbidden: e?.isForbidden === true || httpStatus === 403,
    businessCode: fromResult,
    // ⚠ 顺序有讲究：产品要「原样展示 message」指的是**信封里那枚**。裸 axios 的 `err.message`
    // 是库自己生成的 "Request failed with status code 403"，先读它就会把 axios 的话当成服务方的话显示；
    // `ApiError` 没有 `response` 成员，落到第二项时 `.message` 本来就等于信封的 `message`（见 client.ts）。
    message: e?.response?.data?.message ?? e?.message,
    codeSource: fromResult !== undefined ? 'resultCode' : derivedOnly ? 'code' : 'none',
    httpStatus,
  };
}

/**
 * 业务码取值：**空串与纯空白不算「有值」**（产品明令业务码「禁止写成空串」⇒ 空串＝缺载体），
 * 但**数字 0 是合法码**，不能像 `??` 那样被 falsy 吞掉。
 * ⚠ 本函数现在**只服务 `resultCode` 与那枚取证判断**——`code` 走不走它都不会成为定因。
 */
function pickBusinessCode(value: string | number | undefined | null): string | undefined {
  if (value === undefined || value === null) return undefined;
  if (typeof value === 'number') return Number.isFinite(value) ? String(value) : undefined;
  const text = value.trim();
  return text === '' ? undefined : text;
}

/** 2xx 载荷的形状判定结果。`rows` 交由调用方按各自契约投影，本层不认识任何业务字段。 */
export type ReadShapeVerdict =
  | { kind: 'list'; rows: unknown[] }
  | { kind: 'malformed'; detail: string };

/**
 * 非 2xx 的读态判定。**只读状态与那枚唯一载体，禁止解析 `message` 文案**（文案随语言与措辞变动）。
 * 键名的事在 `readErrorShapeOf` 那一处收口（PRD `:69` 之后**没有"双轨"可言**：定因只认 `resultCode`，
 * `code` 只留一枚取证标志），本层不认识键名，只把「这次有没有载体、message 说了什么」原样带进读态。
 * ⛔ 缺载体时补 `'UNKNOWN'` 之类的占位——那会让渲染面拿到一枚"看着像码"的字符串，而 `:69` 之后
 * 没有人能分辨它是真码还是占位。
 */
export function classifyReadError(error: ReadErrorShape): ReadFailure {
  const base = { code: error.businessCode, message: error.message, codeSource: error.codeSource ?? 'none' };
  if (error.isForbidden) return { kind: 'forbidden', ...base, httpStatus: error.httpStatus ?? 403 };
  return { kind: 'failed', ...base, httpStatus: error.httpStatus };
}

/**
 * 只吃**已解包的 `data`**（`unwrap` 之后）。判定顺序刻意先认数组、再认分页壳（`items` 或 `list`）——
 * 三者都是合法成功支；三者都不是时才判畸形。
 *
 * 分页壳字段：后端统一返回 `{items, page, size, total}`（定义件 §4.2），但历史部分端点仍用 `list`，
 * 两者都认，`items` 优先。
 *
 * ⚠ `:69` 那条「⛔ 拿 `message` 定因」在这里同样成立，不是只在非 2xx 那半边成立：
 * 判畸形的依据是**载荷形状**，⛔ 读信封文案猜"这大概是空名单"。将来若有人把 `:69` 读窄成
 * "只管 `classifyReadError`"，本函数就是最容易开出洞的那一格——成功支一旦开始看文案，
 * 「读不到」与「没有」又会被一句话并掉。
 */
export function classifyReadShape(data: unknown): ReadShapeVerdict {
  if (Array.isArray(data)) return { kind: 'list', rows: data };
  if (data && typeof data === 'object') {
    // 分页壳：优先认 items（后端统一字段），兜底认 list（历史端点）
    const items = (data as { items?: unknown }).items;
    if (Array.isArray(items)) return { kind: 'list', rows: items };
    const list = (data as { list?: unknown }).list;
    if (Array.isArray(list)) return { kind: 'list', rows: list };
    const keys = Object.keys(data as Record<string, unknown>).join(',') || '(空对象)';
    return { kind: 'malformed', detail: `2xx 但载荷里没有 items/list 数组（键：${keys}）` };
  }
  if (data === null) return { kind: 'malformed', detail: '2xx 但 data 为 null' };
  if (data === undefined) return { kind: 'malformed', detail: '2xx 但 data 字段缺席' };
  return { kind: 'malformed', detail: `2xx 但载荷类型是 ${typeof data}，不是数组` };
}

/** 2xx 形状判定 → 状态。`rows.length === 0` 仍记 `ok`：那是契约意义上的真空。 */
export function stateOfReadShape(verdict: { kind: 'list' } | { kind: 'malformed'; detail: string }): ReadState {
  return verdict.kind === 'list' ? { kind: 'ok' } : { kind: 'malformed', detail: verdict.detail };
}

/** #83 判据的机械读法：只有 `ok`（含真空名单）这一格允许出现「没有 X」的空态。 */
export function mayRenderEmptyState(state: ReadState): boolean {
  return state.kind === 'ok';
}

/** 403／其他非 2xx／畸形——三者都「读不到」，都不等于「没有」。 */
export function isUnreadable(state: ReadState): boolean {
  return state.kind === 'forbidden' || state.kind === 'failed' || state.kind === 'malformed';
}

/**
 * 只有 `failed` 值得给「重试」：403 再打一次还是 403（只会诱导反复提单，AC-ROLE-4 那格死锁的成因），
 * `malformed` 是契约对不上、重试也换不来合契约的载荷。
 */
export function mayRetryRead(state: ReadState): boolean {
  return state.kind === 'failed';
}

/**
 * 缺 `resultCode` 时的**定版**固定句（PRD `:69`／产品 seq773 一：「暂时无法获取失败原因」＋原样展示
 * `message`）。导出它是有意的：全仓只许有这一句，⛔ 十处渲染点各写一遍变体；断言也直接钉它，
 * 免得日后有人"顺手改个更准确的措辞"就把产品定版句改丢了。
 */
export const NO_CARRIER_COPY = '暂时无法获取失败原因';

/**
 * 非 2xx 两格的**唯一**成句处。
 * · 有载体 ⇒ 由调用方给出那一句，本函数不动其措辞。⚠ 调用方的成句**也不许**把原始业务码插进去：
 *   用户可见文案一律是业务话术，码与 `detail` 只留在读态结构（`ReadState`）里供排查对账。
 * · 缺载体 ⇒ 整句换成 `NO_CARRIER_COPY`。⛔ **不把客户端自己的措辞续在业务文案后**（口径真源第一节 8：
 *   复核实跑逐字「暂时无法获取失败原因 · 接口未返回统一响应封装（HTTP 500）」违规）——那截尾巴是客户端
 *   抛错时写的实现话（状态码／端点／封装），不是业务事实。`:55` 禁的是拿 `message` 定因；本节禁的是
 *   把**客户端措辞**当业务话说给用户。业务侧给的原话仍可续（测试里的「导出需审批，请先提交导出申请」）。
 *   `message` 本身照旧原样留在读态结构里（`ReadState.message` 字段一个不动），只由渲染出口这一层截掉。
 * ⚠ 分支键是「有没有载体」这一枚事实，⛔ 拿 `codeSource` 参与判定——它是取证位。
 *
 * 导出理由：core 之外还有自己拼句的渲染点（名册那处要额外背一句 AC-ROLE-4 的"别按无审批人提单"），
 * ⛔ 让它再抄一遍这条规则——抄一遍就漏一遍，这正是本层存在的理由。`core/index.ts` 是 `export *`，
 * 外部按 `import { readFailureCopy } from '@/core'` 取用。
 */
/**
 * 客户端自己写的错误措辞（HTTP 状态码、端点路径、封装配额那类实现话）——命中即**不续**给用户看。
 * ⛔ 不要放宽成"凡 `message` 都续"：那正是复核实跑抓到的那句。
 */
const CLIENT_SIDE_MESSAGE = /HTTP\s*\d|\b\d{3}\b|\/[a-z]|接口|封装|响应|端点|字段|契约|本地|浏览器/;

export function readFailureCopy(state: { code?: string; message?: string }, buildWithCarrier: (code: string) => string): string {
  if (state.code !== undefined) return buildWithCarrier(state.code);
  const message = (state.message ?? '').trim();
  if (message === '' || CLIENT_SIDE_MESSAGE.test(message)) return NO_CARRIER_COPY;
  return `${NO_CARRIER_COPY} · ${message}`;
}

/**
 * 「这一格该说什么」的唯一落点。返回 `empty=true` 才允许渲染空态组件；否则 `text` 是**原因**原文。
 *
 * 文案刻意在本层生成：10 处渲染点各自措辞一次，就会各自漏掉某一格一次。业务侧只允许通过
 * `nouns` 提供「X 是什么」这一个词，其余交给这里。
 *
 * ⚠ 缺省三句一律**不插**原始业务码与契约 `detail`（用户可见文案＝业务话术）；码与 detail 仍在
 * `state` 结构里原样保留（`code`／`codeSource`／`detail` 三个字段一个不动），供排查与对账。
 */
export function readStateView(
  state: ReadState,
  nouns: { empty: string; subject?: string; forbidden?: string; failed?: string; malformed?: string },
): { empty: boolean; text: string; tone: 'empty' | 'warning' | 'danger' } {
  const subject = nouns.subject ?? '列表';
  switch (state.kind) {
    case 'forbidden':
      return {
        empty: false,
        text: nouns.forbidden ?? readFailureCopy(state, () => `无权限读取${subject}`),
        tone: 'warning',
      };
    case 'failed':
      return {
        empty: false,
        text: nouns.failed ?? readFailureCopy(state, () => `${subject}读取失败，请稍后重试`),
        tone: 'danger',
      };
    case 'malformed':
      return {
        empty: false,
        text: nouns.malformed ?? `${subject}返回内容异常，请稍后重试`,
        tone: 'danger',
      };
    case 'ok':
      return { empty: true, text: nouns.empty, tone: 'empty' };
    default:
      // `idle`＝还没读过：既不许说「没有」（没资格），也不许说「读不到」（没发生）。
      // 渲染出口因此认 `isUnreadable`，不认 `text === ''`——后者会让首帧挂出一只空白告警框。
      return { empty: false, text: '', tone: 'empty' };
  }
}
